import { useState, useEffect, useCallback } from "react";
import { Bell, X, AlertTriangle, TrendingDown, Shield, ChevronRight, CheckCheck, Mail, Settings } from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProfitPulse() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showEmailSetup, setShowEmailSetup] = useState(false);
  const [emailAddr, setEmailAddr] = useState("");
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/profit-pulse/alerts?status=all&limit=20`);
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (e) { /* silent */ }
  }, []);

  const runPulseCheck = useCallback(async () => {
    setLoading(true);
    try {
      await fetch(`${API}/api/profit-pulse/check`, { method: "POST" });
      await fetchAlerts();
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [fetchAlerts]);

  useEffect(() => {
    fetchAlerts();
    runPulseCheck();
    const interval = setInterval(fetchAlerts, 60000);
    // Fetch email settings
    fetch(`${API}/api/profit-pulse/email-settings`).then(r => r.json()).then(d => {
      setEmailAddr(d.email || "");
      setEmailEnabled(d.enabled || false);
    }).catch(() => {});
    return () => clearInterval(interval);
  }, [fetchAlerts, runPulseCheck]);

  const saveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      const res = await fetch(`${API}/api/profit-pulse/email-settings`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailAddr, enabled: emailEnabled, send_critical: true, send_warnings: false, daily_digest: true }),
      });
      if (res.ok) toast.success("Email settings saved");
    } catch (e) { toast.error("Failed to save"); }
    setSavingEmail(false);
  };

  const sendTestEmail = async () => {
    try {
      const res = await fetch(`${API}/api/profit-pulse/send-test-email`, { method: "POST" });
      const data = await res.json();
      if (res.ok) toast.success(data.message);
      else toast.error(data.detail || "Failed");
    } catch (e) { toast.error("Failed"); }
  };

  const markRead = async (id) => {
    await fetch(`${API}/api/profit-pulse/alerts/${id}/read`, { method: "PATCH" });
    fetchAlerts();
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/profit-pulse/alerts/mark-all-read`, { method: "POST" });
    fetchAlerts();
  };

  const dismiss = async (id) => {
    await fetch(`${API}/api/profit-pulse/alerts/${id}`, { method: "DELETE" });
    fetchAlerts();
  };

  const [executing, setExecuting] = useState(null);
  const executeSmartAction = async (alertId) => {
    setExecuting(alertId);
    try {
      const res = await fetch(`${API}/api/profit-pulse/alerts/${alertId}/execute`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setActionResult({ alertId, ...data });
        setTimeout(() => setActionResult(null), 5000);
      }
    } catch (e) { /* silent */ }
    setExecuting(null);
    fetchAlerts();
  };
  const [actionResult, setActionResult] = useState(null);

  const severityConfig = {
    critical: { bg: "bg-red-50", border: "border-red-200", icon: AlertTriangle, iconColor: "text-red-500", dot: "bg-red-500" },
    warning: { bg: "bg-amber-50", border: "border-amber-200", icon: TrendingDown, iconColor: "text-amber-500", dot: "bg-amber-500" },
    info: { bg: "bg-zinc-50", border: "border-zinc-200", icon: Shield, iconColor: "text-zinc-500", dot: "bg-zinc-400" },
  };

  return (
    <>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="profit-pulse-bell"
        className="fixed top-4 right-4 z-50 hidden lg:flex items-center gap-1.5 px-3 py-2 bg-white border border-zinc-200 rounded-full shadow-sm hover:shadow-md transition-all duration-200 group"
      >
        <Bell className={cn("w-4 h-4 transition-colors", unreadCount > 0 ? "text-amber-500" : "text-zinc-400 group-hover:text-zinc-600")} />
        <span className="text-xs font-medium text-zinc-600">Pulse</span>
        {unreadCount > 0 && (
          <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full" data-testid="pulse-badge">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <>
          <div className="fixed inset-0 z-50" onClick={() => setOpen(false)} />
          <div
            data-testid="profit-pulse-panel"
            className="fixed top-14 right-4 z-50 w-96 max-h-[70vh] bg-white border border-zinc-200 rounded-xl shadow-xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={cn("w-2 h-2 rounded-full animate-pulse", unreadCount > 0 ? "bg-amber-500" : "bg-emerald-500")} />
                <h3 className="text-sm font-semibold text-zinc-800 tracking-tight">Profit Pulse</h3>
                {unreadCount > 0 && <span className="text-xs text-zinc-400">{unreadCount} unread</span>}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-zinc-400 hover:text-zinc-600 px-2 py-1 rounded hover:bg-zinc-50" data-testid="mark-all-read-btn">
                    <CheckCheck className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-600 p-1 rounded hover:bg-zinc-50">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Action Result Toast */}
            {actionResult && (
              <div className="px-4 py-2.5 bg-emerald-50 border-b border-emerald-100 text-xs text-emerald-700 font-medium flex items-center gap-2" data-testid="action-success-toast">
                <Shield className="w-3.5 h-3.5 text-emerald-600" />
                {actionResult.message}
              </div>
            )}

            {/* Alerts List */}
            <div className="flex-1 overflow-y-auto">
              {alerts.length === 0 ? (
                <div className="py-12 text-center" data-testid="pulse-empty">
                  <Shield className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-zinc-700">All clear</p>
                  <p className="text-xs text-zinc-400 mt-0.5">No profit alerts right now</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {alerts.map((alert) => {
                    const cfg = severityConfig[alert.severity] || severityConfig.info;
                    const Icon = cfg.icon;
                    return (
                      <div
                        key={alert.id}
                        data-testid={`pulse-alert-${alert.id}`}
                        className={cn("px-4 py-3 flex gap-3 transition-colors", !alert.read && cfg.bg)}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          <Icon className={cn("w-4 h-4", cfg.iconColor)} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={cn("text-xs leading-relaxed", alert.read ? "text-zinc-500" : "text-zinc-700 font-medium")}>
                              {alert.message}
                            </p>
                            {!alert.read && <div className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5", cfg.dot)} />}
                          </div>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-[10px] text-zinc-400">
                              {new Date(alert.triggered_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {!alert.read && (
                              <button onClick={() => markRead(alert.id)} className="text-[10px] text-zinc-400 hover:text-zinc-600">
                                Mark read
                              </button>
                            )}
                            <button onClick={() => dismiss(alert.id)} className="text-[10px] text-zinc-400 hover:text-red-500">
                              Dismiss
                            </button>
                          </div>
                          {/* Smart Action */}
                          {alert.smart_action && !alert.read && (
                            <div className="mt-2 p-2 bg-white border border-zinc-200 rounded-lg" data-testid={`smart-action-${alert.id}`}>
                              <p className="text-[10px] font-semibold text-zinc-600 mb-1">{alert.smart_action.title}</p>
                              <p className="text-[10px] text-zinc-400 leading-relaxed mb-1.5">{alert.smart_action.description}</p>
                              <div className="flex items-center justify-between">
                                <span className="text-[9px] text-emerald-600 font-medium">{alert.smart_action.impact}</span>
                                <button
                                  onClick={() => executeSmartAction(alert.id)}
                                  disabled={executing === alert.id}
                                  className="text-[10px] font-semibold text-white bg-zinc-700 hover:bg-zinc-800 px-2.5 py-1 rounded-md transition-colors disabled:opacity-50"
                                  data-testid={`smart-action-btn-${alert.id}`}
                                >
                                  {executing === alert.id ? "Executing..." : alert.smart_action.button_label || "Take Action"}
                                </button>
                              </div>
                            </div>
                          )}
                          {/* Action Result */}
                          {alert.actioned && (
                            <div className="mt-1.5 px-2 py-1 bg-emerald-50 border border-emerald-100 rounded text-[10px] text-emerald-700 font-medium" data-testid={`action-result-${alert.id}`}>
                              Action executed successfully
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-zinc-100 bg-zinc-50/50 space-y-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={runPulseCheck}
                  disabled={loading}
                  data-testid="pulse-check-btn"
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-800 py-1.5 rounded-md hover:bg-zinc-100 transition-colors disabled:opacity-50"
                >
                  {loading ? "Checking..." : "Run Pulse Check"}
                  <ChevronRight className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setShowEmailSetup(!showEmailSetup)}
                  className={cn("p-1.5 rounded-md transition-colors", showEmailSetup ? "bg-zinc-200 text-zinc-700" : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600")}
                  data-testid="email-settings-toggle"
                >
                  <Mail className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Email Settings */}
              {showEmailSetup && (
                <div className="bg-white border border-zinc-200 rounded-lg p-3 space-y-2" data-testid="email-settings-panel">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Email Notifications</p>
                  <div className="flex gap-1.5">
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={emailAddr}
                      onChange={e => setEmailAddr(e.target.value)}
                      className="flex-1 text-xs border border-zinc-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-violet-300"
                      data-testid="email-input"
                    />
                    <button onClick={saveEmailSettings} disabled={savingEmail || !emailAddr} className="text-[10px] font-medium text-white bg-zinc-700 hover:bg-zinc-800 px-2.5 py-1.5 rounded-md disabled:opacity-50" data-testid="save-email-btn">
                      {savingEmail ? "..." : "Save"}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={emailEnabled} onChange={e => setEmailEnabled(e.target.checked)} className="rounded border-zinc-300 text-violet-600 focus:ring-violet-500 w-3 h-3" />
                      <span className="text-[10px] text-zinc-500">Send on critical alerts</span>
                    </label>
                    {emailAddr && (
                      <button onClick={sendTestEmail} className="text-[10px] text-violet-600 hover:text-violet-800 font-medium" data-testid="send-test-email-btn">
                        Send test
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
